import { useEffect, useState } from 'react';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonLabel,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { useParams } from 'react-router-dom';
import { GET_CAREER_ORGANIZATION_PROFILES } from '../../../graphql/queries/socialNetwork';
import {
  CareerOrganizationProfilesPeriodEnum,
  type GetCareerOrganizationProfilesQuery,
  type GetCareerOrganizationProfilesQueryVariables,
} from '../../../types/graphql-generated';
import { NETWORK_PAGE_SIZE } from '../../../appConfig';
import { useInfiniteScrollQuery } from '../../../hooks/useInfiniteScrollQuery';
import { AppSectionHeader } from '../../components/ui/AppSectionHeader';
import { PublicProfileCard } from '../profile/components/PublicProfileCard';
import { CareerOrganizationImage } from './components/CareerOrganizationCard';
import { NetworkList } from './components/NetworkList';

type Period = CareerOrganizationProfilesPeriodEnum;
type Organization = NonNullable<
  GetCareerOrganizationProfilesQuery['careerOrganizationProfiles']
>['careerOrganization'];

const PERIOD_HEADERS: Record<Period, string> = {
  [CareerOrganizationProfilesPeriodEnum.Current]: 'Perfiles vinculados actualmente',
  [CareerOrganizationProfilesPeriodEnum.Past]: 'Perfiles pasados',
};

const PERIOD_EMPTY_MESSAGES: Record<Period, string> = {
  [CareerOrganizationProfilesPeriodEnum.Current]: 'Todavía no hay nadie vinculado actualmente',
  [CareerOrganizationProfilesPeriodEnum.Past]: 'Todavía no hay perfiles pasados',
};

// One list of profiles of the organization page. Each period has its own
// query and infinite scroll.
const OrganizationProfiles = ({
  careerOrganizationId,
  period,
  onOrganizationLoaded,
}: {
  careerOrganizationId: string;
  period: Period;
  onOrganizationLoaded: (organization: Organization | null) => void;
}) => {
  const { data, loading, error, loadMore } = useInfiniteScrollQuery<
    GetCareerOrganizationProfilesQuery,
    GetCareerOrganizationProfilesQueryVariables
  >({
    query: GET_CAREER_ORGANIZATION_PROFILES,
    buildVariables: (offset) => ({ careerOrganizationId, period, limit: NETWORK_PAGE_SIZE, offset }),
    pageSize: NETWORK_PAGE_SIZE,
    append: (previous, next) => {
      const previousPage = previous.careerOrganizationProfiles;
      const nextPage = next.careerOrganizationProfiles;
      if (!previousPage || !nextPage) return next;

      return {
        careerOrganizationProfiles: {
          ...nextPage,
          profiles: [...previousPage.profiles, ...nextPage.profiles],
        },
      };
    },
  });

  const page = data?.careerOrganizationProfiles;
  const profiles = page?.profiles ?? [];

  const loadedOrganization = data === undefined ? undefined : (page?.careerOrganization ?? null);

  useEffect(() => {
    if (loadedOrganization !== undefined) onOrganizationLoaded(loadedOrganization);
  }, [loadedOrganization, onOrganizationLoaded]);

  return (
    <>
      <IonLabel className="mb-3 block text-sm font-semibold uppercase tracking-wide text-gray-500">
        {PERIOD_HEADERS[period]}
      </IonLabel>
      <NetworkList
        loading={loading}
        hasError={Boolean(error)}
        errorMessage="No se ha podido cargar la lista de perfiles"
        count={profiles.length}
        hasMore={profiles.length < (page?.totalCount ?? 0)}
        onLoadMore={loadMore}
        emptyMessage={PERIOD_EMPTY_MESSAGES[period]}
      >
        {profiles.map((profile) => (
          <PublicProfileCard
            key={profile.id}
            profile={profile}
            navigable
            profilePathPrefix="/app/main/red/perfiles"
          />
        ))}
      </NetworkList>
    </>
  );
};

// Page of a career organization, reached from the "Entidades" segment of the
// "Red" tab. It lists the people currently linked to it and the people that
// were in it in the past, each in its own segment with infinite scroll.
export const CareerOrganizationPage = () => {
  const careerOrganizationId = useParams<'careerOrganizationId'>().careerOrganizationId!;
  const [period, setPeriod] = useState<Period>(CareerOrganizationProfilesPeriodEnum.Current);
  // undefined while loading, null when the organization does not exist.
  const [organization, setOrganization] = useState<Organization | null | undefined>(undefined);

  return (
    <IonPage>
      <AppSectionHeader variant="main">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/main/red" />
          </IonButtons>
          <IonTitle className="ml-4">Entidad</IonTitle>
        </IonToolbar>
      </AppSectionHeader>
      <IonContent className="ion-padding">
        {organization === null ? (
          <div className="flex min-h-[200px] items-center justify-center">
            <p className="m-0 text-sm text-slate-500">No se ha encontrado la entidad.</p>
          </div>
        ) : (
          <>
            {organization && (
              <div className="mb-4 flex items-center gap-4">
                <CareerOrganizationImage organization={organization} className="h-16 w-16" />
                <h2
                  className="min-w-0 flex-1 font-heading text-gray-900"
                  style={{ margin: 0, fontSize: '20px', fontWeight: 800, lineHeight: 1.2 }}
                >
                  {organization.name}
                </h2>
              </div>
            )}

            <IonSegment
              value={period}
              onIonChange={(event) => setPeriod(event.detail.value as Period)}
              className="mb-4"
            >
              <IonSegmentButton value={CareerOrganizationProfilesPeriodEnum.Current}>
                <IonLabel>Actualmente</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value={CareerOrganizationProfilesPeriodEnum.Past}>
                <IonLabel>Pasados</IonLabel>
              </IonSegmentButton>
            </IonSegment>

            <OrganizationProfiles
              key={period}
              careerOrganizationId={careerOrganizationId}
              period={period}
              onOrganizationLoaded={setOrganization}
            />
          </>
        )}
      </IonContent>
    </IonPage>
  );
};
